import { TextInput, type TextInputProps } from '@mantine/core';
import { IconClock } from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

/** "1:25" -> 85. Also accepts "1.5" and a bare number of minutes, as the API does. */
export function parseDuration(value: string): number | null {
  const text = value.trim().replace(',', '.');
  if (!text) return null;
  if (text.includes(':')) {
    const [hours, minutes] = text.split(':');
    const h = Number(hours || 0);
    const m = Number(minutes || 0);
    if (!Number.isFinite(h) || !Number.isFinite(m) || m >= 60) return null;
    return Math.round(h * 60 + m);
  }
  const asNumber = Number(text);
  if (!Number.isFinite(asNumber)) return null;
  // A decimal is hours, a whole number of 60 or more is already minutes: "1.5" is ninety
  // minutes, "90" is also ninety minutes, and "2" is two hours.
  if (text.includes('.')) return Math.round(asNumber * 60);
  return asNumber < 60 ? Math.round(asNumber * 60) : Math.round(asNumber);
}

export function formatDuration(minutes: number | null | undefined): string {
  if (minutes == null) return '';
  const sign = minutes < 0 ? '-' : '';
  const absolute = Math.abs(minutes);
  return `${sign}${Math.floor(absolute / 60)}:${String(absolute % 60).padStart(2, '0')}`;
}

/**
 * Flight times are written the way a logbook is written, as hours and minutes.
 *
 * The field holds text while it is being typed and only commits a number once the value
 * parses, so a half-typed "1:" never becomes a wrong duration.
 */
export function DurationInput({
  value,
  onChange,
  ...props
}: Omit<TextInputProps, 'value' | 'onChange'> & {
  value: number | null | undefined;
  onChange: (minutes: number | null) => void;
}) {
  const { t } = useTranslation();
  const [text, setText] = useState(() => formatDuration(value));
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    if (!isEditing) setText(formatDuration(value));
  }, [value, isEditing]);

  const parsed = parseDuration(text);
  const isInvalid = text.trim() !== '' && parsed === null;

  return (
    <TextInput
      {...props}
      value={text}
      placeholder={props.placeholder ?? '0:00'}
      leftSection={<IconClock size={15} />}
      error={props.error ?? (isInvalid ? t('logbook.durationHint') : undefined)}
      onFocus={() => setIsEditing(true)}
      onChange={(event) => {
        const next = event.currentTarget.value;
        setText(next);
        const minutes = parseDuration(next);
        if (minutes !== null || next.trim() === '') onChange(minutes);
      }}
      onBlur={(event) => {
        setIsEditing(false);
        setText(formatDuration(parseDuration(event.currentTarget.value)));
        props.onBlur?.(event);
      }}
      styles={{ input: { fontFamily: 'var(--mantine-font-family-monospace)' } }}
    />
  );
}
